import { useCallback, useEffect, useMemo, useState } from 'react';
import { QueryField, QueryGroup, QueryRule, QuerySource } from '../../types/QueryBuilder';
import {
    convertGroupsToBackendFormat, convertSavedQueryToGroups, createEmptyGroup, generateId, getFieldsByCategory, toQuerySource,
} from '../../utils/queryBuilderUtils';
import { useQueryBuilder } from './useQueryBuilder';
import { useQueryDetails } from './useQueryDetails';
import type { SavedQuery } from './useSavedQueries';

const buildDefaultGroup = (fields: QueryField[], categories: string[], source: QuerySource): QueryGroup => {
    const group = createEmptyGroup();
    if (source === 'SIMPLE') return group;
    const [category] = categories;
    return {
        ...group,
        rules: [{ id: generateId(), category, field: getFieldsByCategory(fields, category)[0]?.name, operator: '=', value: '' }],
    };
};

const defaultOperatorOf = (field: QueryField | undefined): QueryRule['operator'] =>
    (field?.defaultOperator as QueryRule['operator']) || '=';

// Les requêtes modifiées avant correction enregistraient une liste à cocher en « = 'a,b' » : on la relit en « in [a, b] »
const repairJoinedList = (rule: QueryRule, field: QueryField | undefined): Pick<QueryRule, 'operator' | 'value'> => {
    const operator = rule.operator || '=';
    const isJoinedList = operator === '=' && defaultOperatorOf(field) === 'in'
        && typeof rule.value === 'string' && rule.value.includes(',');
    if (!isJoinedList) return { operator, value: rule.value || '' };
    return { operator: 'in', value: String(rule.value).split(',').map(item => item.trim()).filter(Boolean) };
};

const completeLoadedGroups = (groups: QueryGroup[], fields: QueryField[], categories: string[]): QueryGroup[] =>
    groups.map(group => ({
        ...group,
        combinator: 'AND',
        rules: group.rules.map(rule => {
            const field = fields.find(f => f.name === rule.field);
            return {
                ...rule,
                category: field?.Categorie || categories[0],
                field: field ? rule.field : fields[0]?.name,
                ...repairJoinedList(rule, field),
            };
        }),
    }));

// Un critère absent de la requête chargée prend l'opérateur par défaut du champ, comme dans « Nouvelle requête » :
// avec « = », une liste cochée serait enregistrée en « = 'a,b' » et ne serait plus recochée à la réouverture
const upsertFieldRule = (groups: QueryGroup[], field: QueryField | undefined, fieldName: string, data: Partial<QueryRule>): QueryGroup[] =>
    groups.map((group, index) => {
        if (index !== 0) return group;
        const hasRule = group.rules.some(rule => rule.field === fieldName);
        return {
            ...group,
            rules: hasRule
                ? group.rules.map(rule => (rule.field === fieldName ? { ...rule, ...data } : rule))
                : [...group.rules, { id: generateId(), field: fieldName, operator: defaultOperatorOf(field), ...data }],
        };
    });

const toGroupId = (value: string | null | undefined): number | null =>
    value && value !== 'null' ? Number(value) : null;

const criteriaKey = (groups: QueryGroup[]): string => JSON.stringify(convertGroupsToBackendFormat(groups));

export const useQueryEditor = (query: SavedQuery | null) => {
    const builder = useQueryBuilder();
    const { fields, categories, setGroups, resetGroups } = builder;
    const { fetchQueryDetails, loading } = useQueryDetails();
    const [description, setDescription] = useState('');
    const [groupId, setGroupId] = useState<number | null>(null);
    const [source, setSource] = useState<QuerySource>('SIMPLE');
    const [usedInCampaign, setUsedInCampaign] = useState(false);
    // Critères tels que chargés, pour savoir si l'édition les a changés
    const [loadedCriteria, setLoadedCriteria] = useState<string | null>(null);

    useEffect(() => {
        if (!query?.QUERY_ID || !fields.length || !categories.length) return;
        let cancelled = false;

        fetchQueryDetails(String(query.QUERY_ID)).then(details => {
            if (cancelled) return;
            const querySource = toQuerySource(details?.SOURCE ?? query.SOURCE);
            setSource(querySource);
            if (!details) {
                setGroups([buildDefaultGroup(fields, categories, querySource)]);
                return;
            }
            setDescription(details.QUERY_NAME || details.DESCRIPTION || query.QUERY_NAME || query.DESCRIPTION || '');
            setGroupId(toGroupId(details.GROUP_QUERY_ID));
            setUsedInCampaign(!!details.USED_IN_CAMPAIGN);
            const converted = details.conditions ? convertSavedQueryToGroups({ conditions: details.conditions }) : [];
            const loadedGroups = converted[0]?.rules.length
                ? completeLoadedGroups(converted, fields, categories)
                : [buildDefaultGroup(fields, categories, querySource)];
            setGroups(loadedGroups);
            setLoadedCriteria(criteriaKey(loadedGroups));
        });

        return () => {
            cancelled = true;
        };
    }, [query, fields, categories, fetchQueryDetails, setGroups]);

    const updateSimpleField = useCallback(
        (fieldName: string, data: Partial<QueryRule>) =>
            setGroups(prev => upsertFieldRule(prev, fields.find(f => f.name === fieldName), fieldName, data)),
        [fields, setGroups]
    );

    const criteriaChanged = useMemo(
        () => loadedCriteria !== null && criteriaKey(builder.groups) !== loadedCriteria,
        [loadedCriteria, builder.groups]
    );

    const reset = useCallback(() => {
        setDescription('');
        setGroupId(null);
        setSource('SIMPLE');
        setUsedInCampaign(false);
        setLoadedCriteria(null);
        resetGroups();
    }, [resetGroups]);

    return {
        builder, loading, description, setDescription, groupId, setGroupId, source, usedInCampaign, criteriaChanged, updateSimpleField, reset,
    };
};
